import { ITEM_KINDS, uid } from "./blueprint";

// Furnishing styles change which pieces (and how many) get placed per room
export const FURNISH_STYLES = {
  minimal: { label: "Minimal", desc: "Just the essentials, lots of open space" },
  cozy: { label: "Cozy", desc: "Comfortable, a few extra pieces" },
  family: { label: "Family", desc: "Maximised seating & storage" },
};

function kindsFor(name, style = "cozy") {
  const n = (name || "").toLowerCase();
  const S = FURNISH_STYLES[style] ? style : "cozy";
  const pick = (min, cozy, family) => (S === "minimal" ? min : S === "family" ? family : cozy);
  if (/bed|master/.test(n)) return pick(["bed"], ["bed", "wardrobe"], ["bed", "wardrobe", "desk"]);
  if (/living|family|lounge|great/.test(n)) return pick(["sofa"], ["sofa", "table", "chair"], ["sofa", "sofa", "table", "chair", "chair"]);
  if (/kitchen/.test(n)) return pick(["fridge", "stove"], ["fridge", "stove"], ["fridge", "stove", "table"]);
  if (/dining/.test(n)) return pick(["table", "chair", "chair"], ["table", "chair", "chair", "chair", "chair"], ["table", "chair", "chair", "chair", "chair", "chair", "chair"]);
  if (/bath|powder|wc/.test(n)) return ["bathtub"];
  if (/office|study|den/.test(n)) return pick(["desk"], ["desk", "chair"], ["desk", "chair", "chair"]);
  if (/garage|closet|laundry|hall|foyer|stair/.test(n)) return [];
  return pick([], ["table"], ["table", "chair"]);
}

// Grid-pack furniture inside a room so nothing overflows the walls
function packRoom(room, style) {
  const items = [];
  const m = 1;
  let cx = room.x + m, cy = room.y + m, rowH = 0;
  for (const k of kindsFor(room.name, style)) {
    const meta = ITEM_KINDS[k];
    if (!meta) continue;
    let w = Math.min(meta.w, room.w - 2 * m);
    let h = Math.min(meta.h, room.h - 2 * m);
    if (w < 1 || h < 1) continue;
    if (cx + w > room.x + room.w - m) { cx = room.x + m; cy += rowH + 1; rowH = 0; }
    if (cy + h > room.y + room.h - m) break;
    items.push({ id: uid(), kind: k, layer: "furniture", x: Math.round(cx * 2) / 2, y: Math.round(cy * 2) / 2, w, h });
    cx += w + 1;
    rowH = Math.max(rowH, h);
  }
  return items;
}

// One-click auto-furnish: returns furniture items for every room, to scale
export function autoFurnish(rooms, style = "cozy") {
  return (rooms || []).flatMap((r) => packRoom(r, style));
}

// Distance from a point to the nearest edge of a rectangle (0 if inside)
function pointRectDist(px, py, r) {
  const rx = r.x, ry = r.y, rw = r.w || 0, rh = r.h || 0;
  const dx = Math.max(rx - px, 0, px - (rx + rw));
  const dy = Math.max(ry - py, 0, py - (ry + rh));
  return Math.hypot(dx, dy);
}

// Detect furniture pieces that block a doorway/walkway clearance zone.
// Doors need ~3ft of clear approach; windows ~2ft. Returns conflicts per item.
export function checkClearance(rooms, items) {
  const furniture = (items || []).filter((it) => ITEM_KINDS[it.kind]?.furniture);
  const openings = (items || []).filter((it) => it.kind === "door" || it.kind === "window");
  const conflicts = [];
  const blockedIds = new Set();
  for (const o of openings) {
    const clr = o.kind === "door" ? 3 : 2;
    for (const f of furniture) {
      const meta = ITEM_KINDS[f.kind];
      const rect = { x: f.x, y: f.y, w: f.w || meta.w, h: f.h || meta.h };
      if (pointRectDist(o.x, o.y, rect) < clr) {
        blockedIds.add(f.id);
        conflicts.push({
          itemId: f.id,
          openingId: o.id,
          openingKind: o.kind,
          label: meta.label,
          message: `${meta.label} blocks a ${o.kind === "door" ? "doorway" : "window"}`,
        });
      }
    }
  }
  return { conflicts, blockedIds };
}

// Slide any furniture blocking a doorway/window clear of the opening (best-effort, kept in-room)
export function nudgeClear(rooms, items) {
  const openings = (items || []).filter((it) => it.kind === "door" || it.kind === "window");
  const roomFor = (cx, cy) => (rooms || []).find((r) => cx >= r.x && cx <= r.x + r.w && cy >= r.y && cy <= r.y + r.h);
  let moved = 0;
  const out = (items || []).map((it) => {
    const meta = ITEM_KINDS[it.kind];
    if (!meta?.furniture) return it;
    let x = it.x, y = it.y;
    const w = it.w || meta.w, h = it.h || meta.h;
    const r = roomFor(x + w / 2, y + h / 2);
    let changed = false;
    for (let pass = 0; pass < 6; pass++) {
      let conflict = null, worst = 0;
      for (const o of openings) {
        const clr = o.kind === "door" ? 3 : 2;
        const d = pointRectDist(o.x, o.y, { x, y, w, h });
        if (d < clr && (clr - d) > worst) { worst = clr - d; conflict = o; }
      }
      if (!conflict) break;
      const cx = x + w / 2, cy = y + h / 2;
      let dx = cx - conflict.x, dy = cy - conflict.y;
      if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) { dx = 1; dy = 0; }
      if (Math.abs(dx) >= Math.abs(dy)) x += (dx >= 0 ? 1 : -1) * (worst + 0.5);
      else y += (dy >= 0 ? 1 : -1) * (worst + 0.5);
      changed = true;
    }
    if (changed && r) {
      x = Math.max(r.x + 0.5, Math.min(x, r.x + r.w - w - 0.5));
      y = Math.max(r.y + 0.5, Math.min(y, r.y + r.h - h - 0.5));
    }
    if (changed) {
      x = Math.round(x * 2) / 2; y = Math.round(y * 2) / 2;
      if (x !== it.x || y !== it.y) { moved++; return { ...it, x, y }; }
    }
    return it;
  });
  return { items: out, moved };
}
